import { ReactElement, useContext, useEffect, useState } from "react";
import { Card, IconButton, Tooltip } from "@mui/material";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { ICatalog, ICatalogCategory, ICatalogRepository, IEditionResponse, ITreeCatalogCategory } from "../../types";
import { useAsyncFetch } from "../../../../system/useFetch";
import CategoryContent from "./categoryContent";
import { addAlert } from "../../../../actions";
import { useDispatch } from "react-redux";
import { cloneObject, exportFile } from "../../../../system/utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CategoryTreeView from "./categoryTreeView";
import { FormatType, IContentAreaProps } from "../../../../types";
import ExportButton from "../../../../components/exportButton";
import { SystemContext } from "../../../../context/systemContext";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

interface IProps extends IContentAreaProps {
  catalogRepository: ICatalogRepository;
  updateCatalog: () => void;
  wasEdited: boolean;
  setWasEdited: (edited: boolean) => void;
  allowEdit: boolean;
}

const gridStyle = {
  gridTemplateColumns: "400px 1fr",
  gridTemplateRows: "1fr",
  justifyItems: "stretch",
  alignItems: "stretch"
};

const generateCatalogTree = (categories: ICatalogCategory[]): ITreeCatalogCategory[] => {
  const dict: Record<number, ITreeCatalogCategory> = {};

  categories.forEach(cat => {
    if (!dict[cat.categoryID ?? 0]) {
      dict[cat.categoryID ?? 0] = {
        category: cat,
        children: []
      };
    }

    if(cat.parentCategoryID && dict[cat.parentCategoryID]) {
      dict[cat.parentCategoryID].children.push(dict[cat.categoryID ?? 0]);
    }
  });

  return Object.values(dict).filter(x => !x.category.parentCategoryID);
};

let treeCatalogCategory: ITreeCatalogCategory | null = null;

const ConfigPanel = ({ catalogRepository, updateCatalog, wasEdited, setWasEdited, allowEdit, parentSize }: IProps): ReactElement => {
  const dispatch = useDispatch<any>();
  const systemContext = useContext(SystemContext);
  const [ deleteDisabled, setDeleteDisabled ] = useState(false);
  const [category, setCategory] = useState<ICatalogCategory | null>(null);
  const [categoryBackup, setCategoryBackup] = useState<ICatalogCategory | null>(null);
  const [execCommand] = useAsyncFetch<IEditionResponse>();
  const [ getCatalogs ] = useAsyncFetch<ICatalog[]>();
  const [ tree, setTree ] = useState<ITreeCatalogCategory[]>([]);

  useEffect(() => {
    // console.log(catalogRepository.selectedCatalog, category && category.catalogID !== catalogRepository.selectedCatalog.catalogID)

    if(catalogRepository.selectedCatalog && (!category || category && category.catalogID !== catalogRepository.selectedCatalog.catalogID)) {
      const categories = catalogRepository.selectedCatalog.categories;
      const firstCategory = categories.length > 0 ? categories[0] : null;
      const newTree = generateCatalogTree(categories);

      treeCatalogCategory = newTree.length > 0 ? newTree[0] : null;
      setTree(newTree);
      setCategory(firstCategory);
      setCategoryBackup(cloneObject(firstCategory));
    }
  }, [ catalogRepository.selectedCatalog ]);


  const internalUpdate = () => {
    const selectedCatalog = catalogRepository.selectedCatalog;
    if (selectedCatalog) {
      const newTree = generateCatalogTree(selectedCatalog.categories);

      treeCatalogCategory = newTree.length > 0 ? newTree[0] : null;
      setTree(newTree);
    }

    updateCatalog();
  };

  const onResetClick = () => {
    const selectedCatalog = catalogRepository.selectedCatalog;
    if (category && selectedCatalog && categoryBackup) {
      selectedCatalog.categories = selectedCatalog.categories.map<ICatalogCategory>(x => x.categoryID === categoryBackup.categoryID ? categoryBackup : x);
      setCategory(categoryBackup);
      setCategoryBackup(cloneObject(categoryBackup));
      internalUpdate();
      setWasEdited(false);
    }
  };

  const onSaveClick = () => {
    execCommand({
      module: "catalog",
      query: "saveCategory",
      payload: {
        category
      }
    }).then(result => {
      if(result && result.result) {
        setCategoryBackup(cloneObject(category));
        if(catalogRepository.selectedCatalog) {
          internalUpdate();
          setWasEdited(false);
        }
        dispatch(addAlert({ message: "The topic was saved!", severity: "success", hash: undefined }));
      } else {
        dispatch(addAlert({ message: "The topic could not be saved!", severity: "error", hash: undefined }));
      }
    });
  };

  const onAddCategory = (isChild = false) => {
    const selectedCatalog = catalogRepository.selectedCatalog;

    if (selectedCatalog && selectedCatalog.catalogID){
      const newCategory: ICatalogCategory = {
        catalogID: selectedCatalog.catalogID,
        parentCategoryID: isChild ? category?.categoryID  ?? null : null,
        name: "topic",
        hash: "",
        content: { fields: [] },
        products: [],
        version: 1,
        depth: isChild && category ? category.depth + 1 : 0
      };

      execCommand({
        module: "catalog",
        query: "saveCategory",
        payload: {
          category: newCategory
        }
      }).then(response => {
        if(response && response.result) {
          newCategory.categoryID = response.editedID;
          selectedCatalog.categories = [ ...selectedCatalog.categories, newCategory ];

          setCategory(newCategory);
          setCategoryBackup(cloneObject(newCategory));
          internalUpdate();
          setWasEdited(false);
        }
      });
    }
  };

  const updateCategory = () => {
    if (catalogRepository.selectedCatalog) {
      internalUpdate();
    }
  };

  const onRemoveCategory = () => {
    const selectedCatalog = catalogRepository.selectedCatalog;
    if (selectedCatalog){
      systemContext.showMessage({
        title: "Delete",
        color: "danger",
        message: `Do you want to delete the Topic "${category?.name}"?`,
        callback: () => {
          execCommand({
            module: "catalog",
            query: "deleteCategory",
            payload: {
              categoryID: category?.categoryID
            }
          }).then(response => {
            if(response && response.result) {
              selectedCatalog.categories = selectedCatalog.categories.filter(x => x.categoryID !== category?.categoryID);
              setCategory(null);
              setCategoryBackup(null);
              internalUpdate();
              setWasEdited(false);
              dispatch(addAlert({ message: "The topic was deleted!", severity: "success", hash: undefined }));
            } else {
              dispatch(addAlert({ message: "The topic could not be saved!", severity: "error", hash: undefined }));
            }
          });
        }
      });
    }
  };

  const onCategorySelected =  (selectedCategory: ITreeCatalogCategory, canDelete: boolean) => {
    treeCatalogCategory = selectedCategory;
    setCategory(selectedCategory.category);
    setDeleteDisabled(!canDelete);
  };

  const onDuplicateCategory = () => {
    if(category) {
      execCommand({
        module: "catalog",
        query: "duplicateCategory",
        payload: {
          categoryID: category.categoryID
        }
      }).then(response => {
        if(response && response.result && catalogRepository.selectedCatalog) {
          getCatalogs({
            module: "catalog",
            query: "getCatalogs",
            payload: {
              nodeID: catalogRepository.selectedCatalog.nodeID,
              nodeType: catalogRepository.selectedCatalog.nodeType,
              familyID: catalogRepository.selectedCatalog.familyID
            }
          }).then(catalogs => {
            const catalog = catalogs?.find(x => x.catalogID === catalogRepository.selectedCatalog?.catalogID);
            if (catalog && catalogRepository.selectedCatalog) {
              catalogRepository.selectedCatalog.categories = catalog.categories;
              const newCategory = catalog.categories.find(x => x.categoryID === response.editedID) ?? catalog.categories.find(x => x.categoryID === category.categoryID);
              if (newCategory) {
                setCategory(newCategory);
                setCategoryBackup(cloneObject(newCategory));
              } else {
                setCategory(catalog.categories[0]);
                setCategoryBackup(cloneObject(catalog.categories[0]));
              }

              internalUpdate();
              setWasEdited(false);
            }
          });
        }
      });
    }
  };

  const onExportFormat = (format: FormatType) => {
    if (!category) {
      return;
    }

    if (format === "JSON") {
      const base64 = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(category));
      const filename = category.name.replace(/[^a-zA-Z0-9 -_]/g, "");

      exportFile(base64, `${filename}.json`);
    }
  };

  const onExportTopicFormat = (format: FormatType) => {
    if (!treeCatalogCategory) {
      return;
    }

    if (format === "JSON") {
      const base64 = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(treeCatalogCategory));
      const filename = treeCatalogCategory.category.name.replace(/[^a-zA-Z0-9 -_]/g, "");

      exportFile(base64, `${filename}.json`);
    }
  };

  return (
    <div className="display-grid align-items-stretch">
      <div className="display-grid" style={gridStyle}>
        <Card className=" m-1 p-0 " >
          <CardHeader title="Topics" style={{ padding: "0.5em", background: "rgba(0, 0, 0, 0.2)" }}
            action={ allowEdit
              ? <>
                <Tooltip title="Add Topic">
                  <span>
                    <IconButton aria-label="add" color="success" onClick={() => onAddCategory(false)} disabled={!allowEdit || !catalogRepository.catalogs || wasEdited}>
                      <AddIcon/>
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Add Sub-Topic">
                  <span>
                    <IconButton aria-label="addChild" onClick={() => onAddCategory(true)} color="success"
                      disabled={!allowEdit || !category || wasEdited || category && category.depth >= 2}>
                      <AccountTreeIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Duplicate Topic">
                  <span>
                    <IconButton aria-label="addChild" onClick={onDuplicateCategory} color="info"
                      disabled={!allowEdit || !category || wasEdited}>
                      <ContentCopyIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                {/*<Tooltip title="Export to PDF">*/}
                {/*  <span>*/}
                {/*    <IconButton aria-label="addChild" onClick={onDuplicateCategory} color="info"*/}
                {/*      disabled={!category || wasEdited}>*/}
                {/*      <PictureAsPdfIcon />*/}
                {/*    </IconButton>*/}
                {/*  </span>*/}
                {/*</Tooltip>*/}

                <ExportButton formats={[ "JSON" ] } onExportFormat={onExportTopicFormat} enabled={category !== null}/>

                <Tooltip title="Remove Topic">
                  <span>
                    <IconButton aria-label="remove" onClick={onRemoveCategory} color="error" disabled={!allowEdit || !category || deleteDisabled}>
                      <RemoveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
              : <></>
            }/>
          <CardContent>
            <CategoryTreeView category={category} setCategory={onCategorySelected} parentSize={parentSize} tree={tree} wasEdited={wasEdited}/>
          </CardContent>
        </Card>

        <Card className="m-1 p-0">
          <CardHeader title="Detail" style={{ padding: "0.5em", background: "rgba(0, 0, 0, 0.2)" }}
            action={allowEdit
              ? <>
                {/*<Tooltip title="Export to PDF">*/}
                {/*  <span>*/}
                {/*    <IconButton aria-label="save" color="info" onClick={onSaveClick} disabled={!category || wasEdited}>*/}
                {/*      <PictureAsPdfIcon/>*/}
                {/*    </IconButton>*/}
                {/*  </span>*/}
                {/*</Tooltip>*/}
                <Tooltip title="Save">
                  <span>
                    <IconButton aria-label="save" color="success" onClick={onSaveClick} disabled={!wasEdited}>
                      <SaveIcon/>
                    </IconButton>
                  </span>
                </Tooltip>

                <ExportButton formats={[ "JSON" ] } onExportFormat={onExportFormat} enabled={category !== null}/>

                <Tooltip title="Reset">
                  <span>
                    <IconButton aria-label="cancel" color="info" onClick={onResetClick} disabled={!wasEdited}>
                      <RefreshIcon/>
                    </IconButton>
                  </span>
                </Tooltip>
              </>
              : <></>
            }/>
          <CardContent>
            { category && catalogRepository.selectedFamily &&
              <CategoryContent category={category}
                updateCategory={updateCategory}
                wasEdited={wasEdited}
                setWasEdited={setWasEdited}
                family={catalogRepository.selectedFamily}
                allowEdit={allowEdit}
                parentSize={parentSize}/>
            }
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConfigPanel;
